import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, Calendar, Gauge, Fuel, Settings2, Palette, DoorOpen, MapPin,
  BadgeCheck, Phone, MessageCircle, Calculator, CheckCircle2, ChevronLeft, ChevronRight, Play,
  Upload, FileText, X, Loader2,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FinancingForm from "@/components/FinancingForm";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { enquireVehicle } from "@/components/Stock";
import { getUser, authHeaders } from "@/lib/auth";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => (n == null ? "—" : n.toLocaleString("pt-PT"));
const fmtPrice = (n) => n.toLocaleString("pt-PT") + " €";

// Documentos necessários por situação profissional
const DOCS_CONTRATO = [
  "Título de Residência",
  "Cartão de Cidadão",
  "CPLP válida",
  "Contrato de trabalho",
  "3 últimos recibos de vencimento",
  "IRS do ano vigente",
  "Comprovativo de morada (água, luz, internet ou telefone)",
  "Comprovativo de NIB / IBAN",
];

const DOCS_RECIBOS = [
  "Título de Residência",
  "Cartão de Cidadão",
  "CPLP válida",
  "3 últimos recibos verdes",
  "Declaração de início de atividade",
  "IRS do ano vigente",
  "Comprovativo de morada (água, luz, internet ou telefone)",
  "Resumo do e-fatura dos últimos dois anos",
];

export default function VehicleDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const settings = useSiteSettings();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [imgIndex, setImgIndex] = useState(0);
  const [showFinancing, setShowFinancing] = useState(false);
  const user = getUser();

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/vehicles/${id}`)
      .then((r) => setVehicle(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="pt-40 pb-32 text-center text-slate-400" data-testid="detail-loading">A carregar viatura…</div>
        <Footer />
      </div>
    );
  }
  if (notFound || !vehicle) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="pt-40 pb-32 text-center" data-testid="detail-notfound">
          <p className="font-display text-2xl text-[#0072C6]">Viatura não encontrada</p>
          <Link to="/viaturas" className="mt-4 inline-block text-sm font-semibold text-[#0072C6] underline">
            Voltar ao catálogo
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const images = (vehicle.images && vehicle.images.length > 0 ? vehicle.images : [vehicle.image_url]).filter(Boolean);
  const waText = encodeURIComponent(`Olá! Tenho interesse na viatura "${vehicle.title}" (${fmtPrice(vehicle.price)}). Podem dar-me mais informações?`);
  const waUrl = `https://wa.me/${settings.whatsapp.replace(/\D/g, "")}?text=${waText}`;

  const specs = [
    { label: "Ano", value: vehicle.year, icon: Calendar },
    { label: "Quilómetros", value: `${fmt(vehicle.km)} km`, icon: Gauge },
    { label: "Combustível", value: vehicle.fuel, icon: Fuel },
    { label: "Caixa", value: vehicle.transmission, icon: Settings2 },
    { label: "Cilindrada", value: vehicle.displacement ? `${fmt(vehicle.displacement)} cm³` : null, icon: Settings2 },
    { label: "Potência", value: vehicle.power ? `${vehicle.power} cv` : null, icon: Settings2 },
    { label: "Cor", value: vehicle.color || null, icon: Palette },
    { label: "Portas", value: vehicle.doors || null, icon: DoorOpen },
    { label: "Estado", value: vehicle.condition || null, icon: BadgeCheck },
    { label: "Localização", value: vehicle.location || null, icon: MapPin },
  ].filter((s) => s.value);

  return (
    <div className="min-h-screen bg-white text-slate-900 antialiased">
      <Header />
      <main className="pt-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
          <Link to="/viaturas" data-testid="detail-back" className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-[#0072C6]">
            <ArrowLeft className="w-4 h-4" /> Voltar ao catálogo
          </Link>
        </div>

        <div className="max-w-7xl mx-auto px-5 sm:px-8 pb-20 grid lg:grid-cols-12 gap-10">
          {/* Gallery */}
          <div className="lg:col-span-7" data-testid="detail-gallery">
            <div className="relative bg-[#F8FAFC] border border-slate-200">
              <img
                src={images[imgIndex]}
                alt={`${vehicle.title} — fotografia ${imgIndex + 1}`}
                data-testid="detail-main-image"
                className="w-full h-[300px] sm:h-[440px] object-cover"
              />
              {vehicle.badge && (
                <span className="absolute top-4 left-4 bg-[#0072C6] text-white text-xs font-semibold tracking-wider uppercase px-3 py-1.5">
                  {vehicle.badge}
                </span>
              )}
              {images.length > 1 && (
                <>
                  <button onClick={() => setImgIndex((i) => (i - 1 + images.length) % images.length)}
                    data-testid="gallery-prev" aria-label="Anterior"
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white flex items-center justify-center shadow">
                    <ChevronLeft className="w-5 h-5 text-[#0072C6]" />
                  </button>
                  <button onClick={() => setImgIndex((i) => (i + 1) % images.length)}
                    data-testid="gallery-next" aria-label="Seguinte"
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white flex items-center justify-center shadow">
                    <ChevronRight className="w-5 h-5 text-[#0072C6]" />
                  </button>
                </>
              )}
            </div>
            {images.length > 1 && (
              <div className="mt-4 grid grid-cols-4 sm:grid-cols-6 gap-3" data-testid="gallery-thumbs">
                {images.map((src, i) => (
                  <button key={i} onClick={() => setImgIndex(i)} data-testid={`thumb-${i}`}
                    className={`border-2 transition-colors ${i === imgIndex ? "border-[#0072C6]" : "border-transparent"}`}>
                    <img src={src} alt="" className="w-full h-16 object-cover" />
                  </button>
                ))}
              </div>
            )}
            {vehicle.video_url && (
              <div className="mt-6 border border-slate-200 p-5 flex items-center gap-4" data-testid="video-row">
                <span className="w-11 h-11 flex items-center justify-center bg-[#0072C6] text-white shrink-0">
                  <Play className="w-5 h-5" />
                </span>
                <div className="flex-1">
                  <p className="font-semibold text-sm text-[#0072C6]">Vídeo da viatura</p>
                  <a href={vehicle.video_url} target="_blank" rel="noopener noreferrer" data-testid="video-link"
                    className="text-xs text-slate-500 hover:text-[#0072C6] underline break-all">
                    Ver vídeo
                  </a>
                </div>
              </div>
            )}

            {/* Description */}
            {vehicle.description && (
              <div className="mt-10" data-testid="detail-description">
                <h2 className="font-display text-2xl text-[#0072C6] mb-4">Descrição</h2>
                <p className="text-slate-600 leading-relaxed whitespace-pre-line">{vehicle.description}</p>
              </div>
            )}

            {/* Equipment */}
            {vehicle.equipment && vehicle.equipment.length > 0 && (
              <div className="mt-10" data-testid="detail-equipment">
                <h2 className="font-display text-2xl text-[#0072C6] mb-4">Equipamento e Extras</h2>
                <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-2.5">
                  {vehicle.equipment.map((eq, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-slate-600">
                      <CheckCircle2 className="w-4 h-4 text-[#0072C6] shrink-0 mt-0.5" /> {eq}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Financing form (integrated) */}
            {showFinancing && (
              <div className="mt-10" data-testid="detail-financing-form">
                <h2 className="font-display text-2xl text-[#0072C6] mb-5">Solicitar Financiamento</h2>
                <FinancingForm vehicle={vehicle} onClose={() => setShowFinancing(false)} />
              </div>
            )}
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-5">
            <div className="lg:sticky lg:top-24 bg-[#F8FAFC] border border-slate-200 p-7 sm:p-8" data-testid="detail-sidebar">
              {(vehicle.brand || vehicle.model) && (
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-slate-500 mb-2">
                  {[vehicle.brand, vehicle.model].filter(Boolean).join(" · ")}
                </p>
              )}
              <h1 className="font-display text-3xl tracking-tight text-[#0072C6]" data-testid="detail-title">
                {vehicle.title}
              </h1>
              {vehicle.version && <p className="mt-1 text-sm text-slate-500">{vehicle.version}</p>}
              <p className="mt-5 font-display text-4xl text-slate-900" data-testid="detail-price">{fmtPrice(vehicle.price)}</p>

              <div className="mt-8 space-y-4">
                <button
                  data-testid="detail-enquire"
                  onClick={() => { enquireVehicle(vehicle); }}
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#0072C6] text-white font-semibold px-6 py-4 text-sm tracking-wide hover:bg-[#005FA3] transition-colors"
                >
                  <Phone className="w-4 h-4" /> Pedir Informações
                </button>
                <a href={waUrl} target="_blank" rel="noopener noreferrer" data-testid="detail-whatsapp"
                  className="w-full inline-flex items-center justify-center gap-2 border border-[#0072C6] text-[#0072C6] font-semibold px-6 py-4 text-sm tracking-wide hover:bg-[#0072C6] hover:text-white transition-colors">
                  <MessageCircle className="w-4 h-4" /> WhatsApp
                </a>
                <button
                  type="button"
                  data-testid="detail-financing"
                  onClick={() => {
                    if (!user) {
                      sessionStorage.setItem("financing_return", window.location.pathname);
                      navigate("/entrar");
                      return;
                    }
                    setShowFinancing((s) => { const n = !s; if (n) setTimeout(() => { document.querySelector('[data-testid="detail-financing-form"]')?.scrollIntoView({ behavior: "smooth" }); }, 100); return n; });
                  }}
                  className="w-full inline-flex items-center justify-center gap-2 border border-slate-300 text-slate-600 font-semibold px-6 py-4 text-sm tracking-wide hover:border-[#0072C6] hover:text-[#0072C6] transition-colors">
                  <Calculator className="w-4 h-4" /> Solicitar Financiamento
                </button>
              </div>

              <div className="mt-8 pt-7 border-t border-slate-200" data-testid="detail-specs">
                <h2 className="text-xs font-semibold uppercase tracking-[0.25em] text-slate-500 mb-5">Características</h2>
                <div className="grid grid-cols-2 gap-y-4 gap-x-6">
                  {specs.map(({ label, value, icon: Icon }) => (
                    <div key={label} className="flex items-start gap-2.5">
                      <Icon className="w-4 h-4 text-[#0072C6] shrink-0 mt-0.5" />
                      <div>
                        <p className="text-[11px] uppercase tracking-wider text-slate-400">{label}</p>
                        <p className="text-sm font-medium text-[#0072C6]">{value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </aside>
        </div>
      </main>
      <Footer />
    </div>
  );
}
